import { ApiError } from '@/api/errors';
import { getMyMoneyPermissions, type MoneyPermission } from '@/api/mePermissions';
import { getPaymentsConnection, type PaymentsConnectionDescriptor } from '@/api/paymentsV2';

/**
 * DNKADM-0400: the comp manager PIN is checked by the SERVER only (DNKAPI-0555).
 * The console never learns the PIN — only whether one is set — so all it decides
 * is whether to show the field and whether the field must be filled.
 */
export type CompPinGate =
  | { status: 'idle' | 'loading' | 'failed' | 'blocked' }
  | { status: 'ready'; overridePinSet: boolean; configured: boolean };

export function compPinGateFrom(descriptor: PaymentsConnectionDescriptor): CompPinGate {
  return {
    status: 'ready',
    overridePinSet: descriptor.overridePinSet,
    configured: descriptor.configured,
  };
}

/**
 * DNKADM-0471: every role reads `canComp` (DNKAPI-0686) from
 * `GET /v2/admin/me/permissions?locationId` — the server answers it from the same governing-PIN
 * lookup the comp makes (the location's active connection, else the tenant default, pay-api's
 * fallback), so the console never reasons about connections. `"pin"`: the field is required.
 * `true`: no PIN. `false` (STAFF where no PIN governs; the server would refuse with
 * `MANAGER_PIN_NOT_SET`): the comp is `blocked`. An unreadable answer is `failed` — optional,
 * the server decides.
 *
 * A server without `canComp` (older than DNKAPI-0686): OWNER/ADMIN fall back to the payments
 * connection read (DNKADM-0400); STAFF, who cannot read it, ask for the PIN (`"pin"`).
 */
export async function resolveCompPinGate(
  isStaffTier: boolean,
  locationId: string | undefined,
): Promise<CompPinGate> {
  try {
    const permissions = await getMyMoneyPermissions(locationId);
    if (permissions === null) return { status: 'failed' };
    if (permissions.canComp === null) {
      return isStaffTier
        ? compPinGateFromPermission('pin')
        : compPinGateFrom(await getPaymentsConnection(locationId));
    }
    return compPinGateFromPermission(permissions.canComp);
  } catch {
    return { status: 'failed' };
  }
}

function compPinGateFromPermission(canComp: MoneyPermission): CompPinGate {
  if (canComp === false) return { status: 'blocked' };
  return { status: 'ready', overridePinSet: canComp === 'pin', configured: true };
}

/**
 * Whether the Comp payment choice is offered at all (DNKADM-0476 F-62, Peyton 2026-10-27). Where
 * no manager PIN governs, STAFF cannot comp (`blocked`), so the choice is not shown; it is not
 * shown to STAFF while the answer is still loading either, so it never appears and then
 * vanishes. OWNER/ADMIN see it while loading, and everyone sees it when the read failed (the
 * server decides).
 */
export function compChoiceShown(gate: CompPinGate, isStaffTier: boolean): boolean {
  if (gate.status === 'blocked') return false;
  if (isStaffTier && (gate.status === 'idle' || gate.status === 'loading')) return false;
  return true;
}

/**
 * The field is REQUIRED when a PIN governs (`canComp` `"pin"`, or on an older server the
 * connection says a PIN is set). It is shown but optional when the console can't tell: the
 * read failed, or (older server, OWNER/ADMIN) the location has no connection of its own and
 * so runs on the tenant default, whose PIN the server applies. Either way the server decides;
 * a wrong or missing PIN comes back as an error on the field.
 */
export function compPinRequired(gate: CompPinGate): boolean {
  return gate.status === 'ready' && gate.overridePinSet;
}

export function compPinShown(gate: CompPinGate): boolean {
  return (
    gate.status === 'failed' ||
    (gate.status === 'ready' && (gate.overridePinSet || !gate.configured))
  );
}

/**
 * A comp may be submitted once the requirement is known (or unknowable) and a required PIN is
 * typed — never where it is `blocked`.
 */
export function compPinAllowsSubmit(gate: CompPinGate, pin: string): boolean {
  if (gate.status === 'idle' || gate.status === 'loading' || gate.status === 'blocked') {
    return false;
  }
  return !compPinRequired(gate) || pin.trim().length > 0;
}

/** The request field, omitted when nothing was typed. */
export function compOverrideAuth(pin: string): { pin: string } | undefined {
  const trimmed = pin.trim();
  return trimmed.length > 0 ? { pin: trimmed } : undefined;
}

export interface CompPinErrorView {
  message: string;
  retryable: boolean;
}

const COMP_PIN_ERROR_COPY: Readonly<Record<string, string>> = {
  COMP_PIN_REQUIRED: 'Enter the manager PIN.',
  MANAGER_PIN_REQUIRED: 'Enter the manager PIN.',
  COMP_PIN_INVALID: "That PIN isn't right.",
  COMP_PIN_LOCKED: 'Too many tries. Try again in 15 minutes.',
  COMP_PIN_UNVERIFIABLE: "The manager PIN can't be checked right now, so nothing was comped. Try again shortly.",
};

/** The server's comp-PIN refusal as field copy, or null when the error is something else. */
export function compPinErrorView(error: unknown): CompPinErrorView | null {
  if (!(error instanceof ApiError)) return null;
  const code = error.details?.['code'];
  if (typeof code !== 'string') return null;
  const message = COMP_PIN_ERROR_COPY[code];
  if (message === undefined) return null;
  return { message, retryable: code === 'COMP_PIN_UNVERIFIABLE' };
}
